import { getMessaging, getToken, onMessage, type MessagePayload } from 'firebase/messaging';
import { app } from './firebase';
import { waitForServiceWorkerActive, waitForServiceWorkerController } from './service-worker-active';

const SW_PATH = '/firebase-messaging-sw.js';
const TOKEN_ATTEMPTS = 3;

let messagingInstance: ReturnType<typeof getMessaging> | null = null;
let swRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let permissionPromise: Promise<NotificationPermission> | null = null;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function getMessagingInstance() {
  if (typeof window === 'undefined') return null;
  if (messagingInstance) return messagingInstance;
  try {
    messagingInstance = getMessaging(app);
    return messagingInstance;
  } catch {
    return null;
  }
}

/** HTTPS / localhost + Notification + Service Worker (iOS PWA incluso). */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator
  );
}

/**
 * Registra o SW do FCM. No iOS o getToken só funciona depois deste
 * registration estar `activated` — `ready` sozinho pode apontar outro worker.
 */
export async function registerMessagingSw(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  if (swRegistrationPromise) return swRegistrationPromise;

  swRegistrationPromise = (async () => {
    try {
      const existing = await navigator.serviceWorker.getRegistration('/');
      const registration =
        existing?.active?.scriptURL?.includes('firebase-messaging-sw.js')
          ? existing
          : await navigator.serviceWorker.register(SW_PATH, {
              scope: '/',
              updateViaCache: 'none',
            });
      const active = await waitForServiceWorkerActive(registration);
      if (!active && !registration.active) {
        swRegistrationPromise = null;
        return null;
      }
      await navigator.serviceWorker.ready;
      await waitForServiceWorkerController();
      return registration;
    } catch (error) {
      console.warn('FCM: falha ao registrar service worker', error);
      swRegistrationPromise = null;
      return null;
    }
  })();

  return swRegistrationPromise;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isPushSupported()) return 'denied';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  if (permissionPromise) return permissionPromise;

  permissionPromise = Notification.requestPermission().finally(() => {
    permissionPromise = null;
  });
  return permissionPromise;
}

export async function getFCMToken(vapidKey: string): Promise<string | null> {
  if (!isPushSupported() || !vapidKey) return null;
  const registration = await registerMessagingSw();
  if (!registration) return null;
  const messaging = getMessagingInstance();
  if (!messaging) return null;

  for (let attempt = 0; attempt < TOKEN_ATTEMPTS; attempt += 1) {
    try {
      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      if (token) return token;
    } catch (error) {
      const err = error as { code?: string; message?: string };
      console.warn('FCM: falha ao obter token', err.code ?? '', err.message ?? error);
    }
    if (attempt < TOKEN_ATTEMPTS - 1) {
      await waitForServiceWorkerActive(registration);
      await delay(400 * (attempt + 1));
    }
  }
  return null;
}

export function onForegroundMessage(
  callback: (payload: MessagePayload) => void,
): (() => void) | null {
  const messaging = getMessagingInstance();
  if (!messaging) return null;
  return onMessage(messaging, callback);
}
